'use client';

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMarkMessagesSeen } from '@/components/notification-bell';
import { cn } from '@/lib/utils';
import type { Id } from '../../../../convex/_generated/dataModel';

function formatTime(ts: number) {
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

interface DirectThreadProps {
  coachUserId: Id<'users'>;
  coachName: string;
}

function DirectThread({ coachUserId, coachName }: DirectThreadProps) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = useQuery(api.parent.messages.getThread, { coachUserId });
  const sendMsg = useMutation(api.parent.messages.send);
  const markRead = useMutation(api.parent.messages.markRead);

  // Mark read on open
  useEffect(() => {
    markRead({ coachUserId }).catch(() => {});
  }, [coachUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom when messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await sendMsg({ coachUserId, body });
      setBody('');
    } finally {
      setSending(false);
    }
  };

  if (messages == null) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/40 border-border rounded-t-lg">
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold">
          {coachName.charAt(0).toUpperCase()}
        </div>
        <span className="font-medium text-foreground">{coachName}</span>
      </div>

      {/* Message list */}
      <div className="flex flex-col gap-2 p-4 flex-1 overflow-y-auto">
        {(messages ?? []).length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            No messages yet. Say hello to your coach!
          </p>
        ) : (
          (messages ?? []).map((msg) => (
            <div
              key={msg.id}
              className={cn('flex', msg.isFromMe ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[75%] px-3 py-2 rounded-2xl text-sm',
                  msg.isFromMe
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'
                )}
              >
                <p>{msg.body}</p>
                <p className={cn('text-xs mt-1', msg.isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                  {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2 p-3 border-t">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Message your coach..."
          className="flex-1 px-3 py-2 border border-input bg-background text-foreground rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!body.trim() || sending}
          className=""
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

export default function ParentMessagesPage() {
  useMarkMessagesSeen('parent');

  const children = useQuery(api.parent.children.list);
  const firstChildId = children?.[0]?.id ?? null;
  const coachData = useQuery(
    api.parent.messages.getCoachForChild,
    firstChildId ? { childId: firstChildId as Id<'players'> } : 'skip'
  );

  if (children === undefined || coachData === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="w-8 h-8 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Messages</h1>
          <p className="text-sm text-muted-foreground">Chat with your coach</p>
        </div>
      </div>

      {/* Direct Message Thread */}
      {coachData ? (
        <div className="bg-card rounded-lg shadow border border-border overflow-hidden flex flex-col">
          <DirectThread
            coachUserId={coachData.coachUserId}
            coachName={coachData.displayName}
          />
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow p-6 text-center">
          <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No coach assigned yet.</p>
          <p className="text-muted-foreground text-xs mt-1">Your coach will be linked to your child's profile soon.</p>
        </div>
      )}
    </div>
  );
}
